"use client";

import Link from "next/link";
import { useRef } from "react";

export default function Button({
  text,
  variant = "primary",
  href,
  target,
  rel,
  onClick,
  type = "button",
  disabled = false,
  className = "",
  startIcon = null,
  endIcon = null,
}) {
  const buttonRef = useRef(null);

  const buttons = {
    primary: "bg-primary text-black hover:bg-primary/90",
    secondary: "bg-white text-[#06040c] hover:bg-white/90 ",
    border:
      "border-2 border-white/20 text-white hover:bg-white/16 hover:border-transparent",
    dark: "bg-accent text-white hover:bg-accent/90",
  };

  const createRipple = (e) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const ripple = document.createElement("span");

    const size = Math.max(rect.width, rect.height) * 2.2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.className = "ripple";
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    button.appendChild(ripple);

    ripple.addEventListener("animationend", () => {
      ripple.remove();
    });
  };

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    createRipple(e);
    onClick?.(e);
  };

  const buttonClassName = `relative inline-flex h-14 w-fit ${disabled ? "cursor-not-allowed" : "cursor-pointer"} items-center justify-center gap-3 overflow-hidden rounded-full px-8 py-3 text-lg font-semibold transition-colors duration-300 ${buttons[variant]} ${className}`;

  if (href) {
    const isExternalHref = /^(https?:|mailto:|tel:)/.test(href);

    if (isExternalHref) {
      return (
        <a
          ref={buttonRef}
          href={href}
          target={target}
          rel={rel ?? (target === "_blank" ? "noreferrer" : undefined)}
          onClick={handleClick}
          className={buttonClassName}
        >
          {startIcon && (
            <span className="relative z-10 flex items-center">{startIcon}</span>
          )}
          <span className="relative z-10">{text}</span>
          {endIcon && (
            <span className="relative z-10 flex w-4 items-center">
              {endIcon}
            </span>
          )}
        </a>
      );
    }

    return (
      <Link
        ref={buttonRef}
        href={href}
        onClick={handleClick}
        className={buttonClassName}
      >
        {startIcon && (
          <span className="relative z-10 flex items-center">{startIcon}</span>
        )}
        <span className="relative z-10">{text}</span>
        {endIcon && (
          <span className="relative z-10 flex w-4 items-center">{endIcon}</span>
        )}
      </Link>
    );
  }

  return (
    <button
      ref={buttonRef}
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={buttonClassName}
    >
      {startIcon && (
        <span className="relative z-10 flex items-center">{startIcon}</span>
      )}
      <span className="relative z-10">{text}</span>
      {endIcon && (
        <span className="relative z-10 flex items-center w-4">{endIcon}</span>
      )}
    </button>
  );
}
